'use client';

import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';

import { AppBanner } from './AppBanner.client';
import { CompactAppBanner } from './CompactAppBanner.client';
import type { AppBannerVariant } from './types';
import { useAppBannerVariant } from './useAppBannerVariant';

type AppBannerSlotProps = {
  position: Exclude<AppBannerVariant, 'control'>;
};

const AppBannerTestSlot = ({ position }: AppBannerSlotProps) => {
  const variant = useAppBannerVariant();

  if (!variant) {
    return null;
  }

  if (variant === 'control') {
    return position === 'above' ? <AppBanner /> : null;
  }

  return position === variant ? <CompactAppBanner variant={variant} /> : null;
};

/**
 * Renders the AppBanner design for the current A/B/C test variant, but only at its designated
 * position relative to the header: control keeps the existing banner above the header, variant
 * "above" renders the new design above the header, variant "below" renders it below the header.
 */
export const AppBannerSlot = ({ position }: AppBannerSlotProps) => {
  const {
    abTesting: { appBannerAbTestId: testId },
  } = useConfig();

  if (!testId) {
    return position === 'above' ? <AppBanner /> : null;
  }

  return <AppBannerTestSlot position={position} />;
};
